import { useCallback, useEffect, useRef } from "react";

import useUploadedFilesBulkCreateProfiles from "@/components/uploads/useUploadedFilesBulkCreateProfiles";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { type FileUploadState, useFileUpload } from "@/data/media/useFileUpload";
import { ProfileUploadsContext, type UploadBatch } from "@/data/uploads/uploads.context";

const useBackgroundProfileUploads = () => {
  const {
    uploadQueue,
    setBatchProcessing,
    setCurrentUploadBatch,
    updateQueue,
    showMinimizedWindow,
    setShowMinimizedWindow,
    currentUploadBatch,
  } = ProfileUploadsContext();
  const requestUploadMutation = BizProfileQueries.useRequestUpload();
  const { processCompletedBatch } = useUploadedFilesBulkCreateProfiles();

  const showMinizedWindowRef = useRef(false);

  const currentBatchId = currentUploadBatch?.id;
  const isCurrentBatchProcessing = currentUploadBatch?.isProcessing;
  const updateFileUploadStatus = (fileName: string, status: FileUploadState) => {
    setCurrentUploadBatch((curr) => {
      if (curr) {
        const updatedFiles = curr.files.map((file) => (file.file.name === fileName ? { ...file, status } : file));
        return { ...curr, files: updatedFiles };
      }
      return curr;
    });
  };

  const { uploadFile } = useFileUpload(requestUploadMutation, {
    onSuccess: (e, state) => {
      updateFileUploadStatus(e.fileName, state.state);
    },
    onChange: (e, state) => {
      updateFileUploadStatus(e.fileName, state.state);
    },
    onError: (e) => {
      updateFileUploadStatus(e.fileName, "error");
    },
  });

  const uploadBatchFiles = useCallback(async (batch: UploadBatch) => {
    const mediaIds: string[] = [...batch.uploadedMediaIds]; // Start with existing media IDs

    for (const fileItem of batch.files) {
      if (fileItem.status === "idle") {
        const mediaId = await uploadFile(fileItem.file);
        if (mediaId) {
          mediaIds.push(mediaId);
          setCurrentUploadBatch((curr) => {
            if (curr) {
              return { ...curr, uploadedMediaIds: mediaIds };
            }
            return curr;
          });
        }
      }
    }
    setCurrentUploadBatch((curr) => {
      if (curr) {
        const updatedBatch = { ...curr, uploadedMediaIds: mediaIds, hasBeenProcessed: true, isProcessing: false };
        if (showMinizedWindowRef.current) {
          processCompletedBatch(updatedBatch);
        }
        updateQueue(curr.id, updatedBatch);
        return updatedBatch;
      }
      return curr;
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    showMinizedWindowRef.current = showMinimizedWindow;
  }, [showMinimizedWindow]);

  useEffect(() => {
    const unprocessedBatches = uploadQueue?.filter((batch) => !batch.hasBeenProcessed && !batch.isProcessing) || [];

    if (isCurrentBatchProcessing) {
      return;
    }

    if (!uploadQueue?.length) {
      setCurrentUploadBatch(null);
      setShowMinimizedWindow(false);
    }

    if (unprocessedBatches.length > 0) {
      const nextBatch = unprocessedBatches[0];
      if (nextBatch?.isProcessing) {
        return;
      }
      nextBatch.isProcessing = true;
      setCurrentUploadBatch(nextBatch);
      setBatchProcessing(nextBatch.id, true);
      uploadBatchFiles(nextBatch);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBatchId, isCurrentBatchProcessing, uploadQueue]);
};

export default useBackgroundProfileUploads;
